/**
 * Reiter „Überblick“ der Seite Gedächtnis (Mockup freigegeben 25.09.2026, Artifact
 * UXqy6s3qxdztdoVQKAKhi9): was MAESTRO gelernt hat, woher, und was davon neu ist.
 *
 * Eine Route liefert alles (GET /api/gedaechtnis/ueberblick). Der Besuch wird beim Öffnen gemerkt
 * (POST /api/gedaechtnis/besuch) — dessen Rückgabe („vorher“) ist der Zeitpunkt für „Neu seit
 * deinem letzten Besuch“, je Benutzer. Urteile gehen über die bestehenden Status-Routen, Löschen
 * und Profil-Korrektur über die neuen (sql/525); die Rolle prüft der Server, die Oberfläche
 * blendet nur aus, was ohnehin abgelehnt würde.
 *
 * Gezählte Wirkung zeigt nur, was gemessen ist (Playbook-Wirkung aus der Interaktionsspur) —
 * keine Schätzung, wie oft etwas „wohl“ berücksichtigt wurde.
 */
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useApi, apiMutate } from "../hooks/useApi";
import { Button, Card, ErrorNote, Icon, Input, KpiCard, LoadingRows, Zeichenzaehler, useBestaetigung, Textarea } from "../ui/components";
import { useContentRefetch, useCopilot } from "../ui/CopilotProvider";

type NeuArt = "fakt" | "einsicht" | "regel" | "stil";
export interface NeuEintrag {
  art: NeuArt;
  id: string;
  text: string;
  detail?: string | null;
  status: string;
  herkunft: "mensch" | "beobachtet" | "referenzen";
  datum: string;
  vorgaenge: string[];
  anzahlBelege?: number;
}
export interface Ueberblick {
  darfVerwalten: boolean;
  zuletztBesucht: string | null;
  seit: string;
  zahlen: {
    fakten: { bestaetigt: number; unbestaetigt: number; betrieb: number; kundenFakten: number; kunden: number; zuletzt: string | null };
    einsichten: { aktiv: number; vorschlaege: number };
    regeln: { aktiv: number; entwurf: number; zuletzt: string | null };
    katalog: { positionen: number; ausKiImport: number; zuletzt: string | null };
    stil: { aktiv: number; entwurf: number; zuletzt: string | null };
    websiteChat: { veroeffentlicht: number; intern: number; zuletzt: string | null };
  };
  neu: NeuEintrag[];
  betriebsprofil: { inhalt: string; version: number; updatedAt: string } | null;
  wirkung: { id: string; name: string; vorgangstyp: string; vorherQuote: number | null; nachherQuote: number | null }[];
  hinweise: { art: "veraltet"; id: string; text: string; seit: string }[];
  /** Lernen S1 (Entscheidung 6): KI-Entwurf gegen gesendete Fassung. */
  lernen?: LernKennzahl;
}
export interface LernKennzahl {
  ausgewertet: number;
  streng: number;
  inhaltlich: number;
  ausgeschlossen: number;
  verlauf: { wocheAb: string; ausgewertet: number; streng: number; inhaltlich: number }[];
}

const prozent = (teil: number, ganz: number) => (ganz > 0 ? `${Math.round((teil / ganz) * 100)} %` : "—");

/**
 * Lernen S1 — „Wie oft gehen KI-Entwürfe unverändert raus?“ Zwei Zahlen (Antoines Entscheidung
 * E4 B): streng (kein Feld geändert) und inhaltlich (gleiche Gerichte und Mengen je Gast). Der
 * Verlauf zeigt je Woche die inhaltliche Quote; die Zahl je Balken steht im Hover und in der
 * Tabelle für Screenreader.
 */
export function LernKennzahlKarte({ k }: { k: LernKennzahl }) {
  const wochen = k.verlauf;
  const kw = (iso: string) => new Date(`${iso}T00:00:00`).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit" });
  return (
    <Card className="space-y-4 p-5" data-testid="lern-kennzahl">
      <h2 className="flex items-center gap-2 font-body-lg text-body-lg font-semibold text-on-background dark:text-inverse-on-surface">
        <Icon name="psychology" className="text-primary" /> Wie oft KI-Entwürfe unverändert rausgehen
      </h2>
      {k.ausgewertet === 0 ? (
        <p className="font-body-sm text-body-sm text-on-surface-variant">
          Noch keine Messwerte. Gezählt wird ab jetzt: sobald ein KI-Entwurf gesendet wird, steht hier, ob er so rausging, wie MAESTRO ihn vorgeschlagen hat.
        </p>
      ) : (
        <div className="grid gap-4 sm:grid-cols-[auto_auto_1fr] sm:items-end">
          <div>
            <p className="font-headline-md text-[32px] font-semibold tabular-nums text-on-background dark:text-inverse-on-surface">{prozent(k.inhaltlich, k.ausgewertet)}</p>
            <p className="font-body-sm text-body-sm text-on-surface-variant">inhaltlich unverändert</p>
            <p className="text-[12px] text-on-surface-variant/80">{k.inhaltlich} von {k.ausgewertet} · gleiche Gerichte und Mengen</p>
          </div>
          <div>
            <p className="font-headline-md text-[32px] font-semibold tabular-nums text-on-background dark:text-inverse-on-surface">{prozent(k.streng, k.ausgewertet)}</p>
            <p className="font-body-sm text-body-sm text-on-surface-variant">streng unverändert gesendet</p>
            <p className="text-[12px] text-on-surface-variant/80">{k.streng} von {k.ausgewertet} · kein Feld geändert</p>
          </div>
          <div className="min-w-0">
            <div className="flex h-20 items-end gap-[2px]" aria-hidden="true">
              {wochen.map((w) => (
                <div
                  key={w.wocheAb}
                  title={`Woche ab ${kw(w.wocheAb)}: ${w.ausgewertet ? `${w.inhaltlich} von ${w.ausgewertet} inhaltlich unverändert` : "keine gesendeten KI-Entwürfe"}`}
                  className="flex h-full flex-1 items-end"
                >
                  <div
                    className={`w-full rounded-t-[4px] ${w.ausgewertet ? "bg-primary" : "bg-outline-variant/50"}`}
                    style={{ height: w.ausgewertet ? `${Math.max(6, (w.inhaltlich / w.ausgewertet) * 100)}%` : "3px" }}
                  />
                </div>
              ))}
            </div>
            <div className="mt-1 flex justify-between text-[11px] text-on-surface-variant">
              <span>ab {kw(wochen[0]!.wocheAb)}</span>
              <span>inhaltlich unverändert je Woche</span>
              <span>{kw(wochen[wochen.length - 1]!.wocheAb)}</span>
            </div>
            <table className="sr-only">
              <caption>Inhaltlich unverändert gesendete KI-Entwürfe je Woche</caption>
              <tbody>
                {wochen.map((w) => (
                  <tr key={w.wocheAb}><td>{kw(w.wocheAb)}</td><td>{w.inhaltlich} von {w.ausgewertet}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      <p className="text-[12px] text-on-surface-variant">
        Verglichen wird der KI-Vorschlag mit der Fassung, die an den Gast ging.
        {k.ausgeschlossen > 0 ? ` ${k.ausgeschlossen} ausgeschlossen (Test oder „Nicht zum Lernen verwenden“).` : " Tests und Angebote mit „Nicht zum Lernen verwenden“ zählen nicht."}
      </p>
    </Card>
  );
}

const datum = (iso: string | null | undefined) => (iso ? new Date(iso).toLocaleDateString("de-DE") : "—");
const quote = (q: number | null) => (q === null ? "zu wenig Daten" : `${Math.round(q * 100)} %`);

const BADGE = "rounded-full px-2 py-0.5 font-label-md text-label-md";
const HERKUNFT: Record<NeuEintrag["herkunft"], { label: string; klasse: string }> = {
  mensch: { label: "Von Ihnen", klasse: "bg-secondary-container text-on-secondary-container" },
  beobachtet: { label: "Beobachtet", klasse: "bg-primary-container text-on-primary-container" },
  referenzen: { label: "Aus Referenzen", klasse: "bg-tertiary-container text-on-tertiary-container" },
};
const STATUS: Record<string, string> = {
  unverified: "noch nicht bestätigt",
  disputed: "von Ihnen bestritten",
  offen: "noch nicht bestätigt",
  vorschlag: "Vorschlag, wirkt noch nicht",
  draft: "wartet auf Freigabe",
};
const ART_LABEL: Record<NeuArt, string> = { fakt: "Fakt", einsicht: "Muster", regel: "Regel-Entwurf", stil: "Stil des Hauses" };

function Beleg({ e }: { e: NeuEintrag }) {
  if (e.art === "stil") {
    return <>Gebaut aus {e.anzahlBelege ?? 0} hochgeladenen Unterlagen</>;
  }
  if (e.art === "regel") return <>{e.herkunft === "beobachtet" ? "Vom Gedächtnis vorgeschlagen, nicht von Ihnen" : "Von Ihrem Team angelegt"}</>;
  if (e.vorgaenge.length === 0) {
    if (e.art === "einsicht" && e.anzahlBelege) return <>Gestützt auf {e.anzahlBelege} gezählte Reaktionen</>;
    return <>{e.herkunft === "mensch" ? "Von Ihnen gesagt" : "Aus Ihren Entscheidungen gezählt"}</>;
  }
  return (
    <>
      Beleg:{" "}
      {e.vorgaenge.map((id, i) => (
        <span key={id}>
          {i > 0 && ", "}
          <Link to={`/vorgaenge/${id}`} className="font-semibold text-primary hover:underline">
            Vorgang {i + 1}
          </Link>
        </span>
      ))}
    </>
  );
}

function Eintrag({ e, darfVerwalten, onFertig }: { e: NeuEintrag; darfVerwalten: boolean; onFertig: () => void }) {
  const { bestaetigen, dialog: bestaetigungsDialog } = useBestaetigung();
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const [korrektur, setKorrektur] = useState<string | null>(null);

  async function tu(fn: () => Promise<unknown>) {
    setBusy(true);
    setFehler(null);
    try {
      await fn();
      onFertig();
    } catch (err) {
      setFehler(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }
  const urteil = (u: "stimmt" | "stimmt_nicht" | "ausblenden") =>
    tu(() => apiMutate(`/api/gedaechtnis/${e.art}/${e.id}/status`, "POST", { urteil: u }));
  const loeschen = async () => {
    if (
      !(await bestaetigen({
        titel: "Endgültig löschen?",
        text: "MAESTRO merkt sich das und lernt diesen Eintrag nicht wieder.",
        bestaetigen: "Löschen",
        gefahr: true,
      }))
    )
      return;
    void tu(() => apiMutate(`/api/gedaechtnis/${e.art}/${e.id}`, "DELETE"));
  };
  // Korrigieren = neuer, von Ihnen bestätigter Fakt, der alte wird ausgeblendet (dieselbe
  // Reihenfolge wie auf „Was MAESTRO weiß“: lieber kurz doppelt als verloren).
  const korrigieren = (text: string) =>
    tu(async () => {
      await apiMutate("/api/gedaechtnis/fakt", "POST", { aussage: text, anlass: "korrektur" });
      await apiMutate(`/api/gedaechtnis/fakt/${e.id}/status`, "POST", { urteil: "ausblenden" });
    });

  const statusText = STATUS[e.status];
  const knopf = "inline-flex items-center gap-1 rounded-full border border-outline-variant px-3 py-1.5 text-[12px] font-semibold disabled:opacity-40 dark:border-white/10";
  return (
    <div className="rounded-2xl border border-outline-variant bg-surface-container-lowest px-4 py-3.5 dark:border-white/10 dark:bg-white/[0.04]" data-testid="gedaechtnis-neu">
      {bestaetigungsDialog}
      <div className="flex flex-wrap items-center gap-2">
        <span className={`${BADGE} ${HERKUNFT[e.herkunft].klasse}`}>{HERKUNFT[e.herkunft].label}</span>
        <span className={`${BADGE} bg-surface-container-high text-secondary dark:bg-white/[0.08]`}>{ART_LABEL[e.art]}</span>
        {statusText && <span className={`${BADGE} bg-[#F0EAE0] text-[#6F675A] dark:bg-white/[0.08] dark:text-[#b8ae9e]`}>{statusText}</span>}
        <span className="ml-auto text-[12px] text-on-surface-variant">{datum(e.datum)}</span>
      </div>
      {korrektur === null ? (
        <p className="mt-2 font-body-md text-body-md font-semibold text-on-background dark:text-inverse-on-surface">{e.text}</p>
      ) : (
        <form
          className="mt-2 flex flex-wrap gap-2"
          onSubmit={(ev) => {
            ev.preventDefault();
            if (korrektur.trim().length >= 3) void korrigieren(korrektur.trim());
          }}
        >
          <label className="min-w-[240px] flex-1">
            <Input value={korrektur} maxLength={500} onChange={(ev) => setKorrektur(ev.target.value)} aria-label="Korrigierter Text" />
            <Zeichenzaehler current={korrektur.length} max={500} />
          </label>
          <Button type="submit" disabled={busy || korrektur.trim().length < 3}>Speichern</Button>
          <Button type="button" variant="secondary" onClick={() => setKorrektur(null)}>Abbrechen</Button>
        </form>
      )}
      {e.detail && e.art !== "stil" && <p className="mt-1 line-clamp-3 font-body-sm text-body-sm text-on-surface-variant">{e.detail}</p>}
      <p className="mt-1.5 flex flex-wrap items-center gap-1 text-[12.5px] text-on-surface-variant">
        <Icon name="fact_check" className="text-[15px]" />
        <Beleg e={e} />
      </p>
      {fehler && <p className="mt-1.5 font-body-sm text-body-sm text-error">{fehler}</p>}
      <div className="mt-2 flex flex-wrap gap-2">
        {(e.art === "fakt" || e.art === "einsicht") && (
          <>
            {e.status !== "confirmed" && (
              <button type="button" className={`${knopf} border-primary bg-primary text-on-primary`} disabled={busy} onClick={() => void urteil("stimmt")}>
                <Icon name="check" className="text-[14px]" /> Stimmt
              </button>
            )}
            {e.art === "fakt" && (
              <button type="button" className={knopf} disabled={busy} onClick={() => setKorrektur(e.text)}>
                <Icon name="edit" className="text-[14px]" /> Korrigieren
              </button>
            )}
            {/* E9: umkehrbar, daher neutral — Rot bleibt dem Loeschen vorbehalten. */}
            <button type="button" className={knopf} disabled={busy} onClick={() => void urteil("stimmt_nicht")}>
              <Icon name="close" className="text-[14px]" /> Stimmt nicht
            </button>
            <button type="button" className={knopf} disabled={busy} onClick={() => void urteil("ausblenden")}>
              <Icon name="visibility_off" className="text-[14px]" /> Abschalten
            </button>
            {darfVerwalten && (
              <button type="button" className={`${knopf} text-error`} disabled={busy} onClick={loeschen}>
                <Icon name="delete" className="text-[14px]" /> Löschen
              </button>
            )}
          </>
        )}
        {e.art === "regel" && (
          <Link to="/gedaechtnis?tab=handelt" className={knopf}>
            <Icon name="play_arrow" className="text-[14px]" /> Prüfen{darfVerwalten ? " & aktivieren" : " (aktivieren nur Inhaber)"}
          </Link>
        )}
        {e.art === "stil" && (
          <Link to="/gedaechtnis?tab=lernt" className={knopf}>
            <Icon name="task_alt" className="text-[14px]" /> Prüfen{darfVerwalten ? " & freigeben" : " (freigeben nur Inhaber)"}
          </Link>
        )}
      </div>
      {e.art === "fakt" && e.status === "unverified" && (
        <p className="mt-1.5 text-[12px] text-on-surface-variant">Unbestätigte Fakten nutzt MAESTRO nur beim betreffenden Vorgang, nicht betriebsweit.</p>
      )}
      {e.art === "einsicht" && e.status === "vorschlag" && (
        <p className="mt-1.5 text-[12px] text-on-surface-variant">Stützt sich auf den Absagegrund eines Gastes — wirkt erst, wenn Sie „Stimmt“ sagen.</p>
      )}
    </div>
  );
}

function Betriebsprofil({ profil, darfVerwalten, onFertig }: { profil: Ueberblick["betriebsprofil"]; darfVerwalten: boolean; onFertig: () => void }) {
  const [text, setText] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  async function speichern() {
    if (text === null) return;
    setBusy(true);
    setFehler(null);
    try {
      await apiMutate("/api/gedaechtnis/betriebsprofil", "PUT", { inhalt: text });
      setText(null);
      onFertig();
    } catch (err) {
      setFehler(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }
  return (
    <Card className="space-y-3 p-5">
      <h2 className="flex items-center gap-2 font-body-lg text-body-lg font-semibold text-on-background dark:text-inverse-on-surface">
        <Icon name="psychology" className="text-primary" /> So beschreibt MAESTRO Ihren Betrieb
      </h2>
      {text === null ? (
        <p className="whitespace-pre-line rounded-xl bg-surface-container-low p-3 font-body-sm text-body-sm text-on-background dark:bg-white/[0.04] dark:text-inverse-on-surface">
          {profil?.inhalt ?? "Noch kein Profil — es entsteht aus Ihren abgeschlossenen Vorgängen."}
        </p>
      ) : (
        <label className="block">
          <Textarea rows={6} maxLength={4000} value={text} onChange={(e) => setText(e.target.value)} aria-label="Betriebsprofil" />
          <Zeichenzaehler current={text.length} max={4000} />
        </label>
      )}
      {profil && <p className="text-[12.5px] text-on-surface-variant">Zuletzt geändert am {datum(profil.updatedAt)} · Version {profil.version}</p>}
      {fehler && <ErrorNote message={fehler} />}
      {darfVerwalten && (
        <div className="flex gap-2">
          {text === null ? (
            <Button type="button" variant="secondary" onClick={() => setText(profil?.inhalt ?? "")}>
              <Icon name="edit" className="text-[16px]" /> Korrigieren
            </Button>
          ) : (
            <>
              <Button type="button" disabled={busy || text.trim().length === 0} onClick={() => void speichern()}>{busy ? "Speichert …" : "Speichern"}</Button>
              <Button type="button" variant="secondary" onClick={() => setText(null)}>Abbrechen</Button>
            </>
          )}
        </div>
      )}
      <p className="text-[12px] text-on-surface-variant">Dieses Profil liest der Assistent bei jeder Antwort mit. Ihre Korrektur ist die Grundlage, auf der er weiterlernt.</p>
    </Card>
  );
}

export function GedaechtnisUeberblick() {
  // Besuch EINMAL je Seitenaufruf merken (StrictMode ruft Effekte doppelt) — der vorige
  // Zeitpunkt ist die Grenze für „Neu seit Ihrem letzten Besuch“.
  const gemerkt = useRef(false);
  const [seit, setSeit] = useState<{ wert: string | null } | null>(null);
  const { emitContentChanged } = useCopilot();
  useEffect(() => {
    if (gemerkt.current) return;
    gemerkt.current = true;
    apiMutate<{ vorher: string | null }>("/api/gedaechtnis/besuch", "POST")
      .then((d) => {
        setSeit({ wert: d?.vorher ?? null });
        // Der Menü-Zähler „n neu“ (AppShell.tsx) geht damit auf 0.
        emitContentChanged({ resource: "gedaechtnis_besuch" });
      })
      .catch(() => setSeit({ wert: null }));
  }, []);
  const q = useApi<Ueberblick>(seit ? `/api/gedaechtnis/ueberblick${seit.wert ? `?seit=${encodeURIComponent(seit.wert)}` : ""}` : null);
  useContentRefetch("knowledge", () => q.reload());

  if (q.error) return <Card className="overflow-hidden"><ErrorNote zentriert title="Das Gedächtnis konnte nicht geladen werden" message={q.error} onRetry={q.reload} /></Card>;
  if (!q.data) return <Card className="overflow-hidden"><LoadingRows rows={4} /></Card>;
  const d = q.data;
  const z = d.zahlen;
  const neuFakten = d.neu.filter((n) => n.art === "fakt").length;
  const neuMuster = d.neu.filter((n) => n.art === "einsicht").length;

  const bereiche = [
    { bereich: "Betrieb & Regeln", eintraege: `${z.regeln.aktiv} Regeln aktiv, ${z.fakten.betrieb} bestätigte Fakten, ${z.einsichten.aktiv} Muster`, wirkt: "Chat, Angebotsentwurf", zuletzt: z.fakten.zuletzt ?? z.regeln.zuletzt },
    { bereich: "Speisen & Katalog", eintraege: `${z.katalog.positionen} Positionen`, wirkt: "Angebotsentwurf, Chat", zuletzt: z.katalog.zuletzt },
    { bereich: "Kunden", eintraege: `${z.fakten.kundenFakten} Fakten zu ${z.fakten.kunden} Kunden`, wirkt: "nur beim jeweiligen Kunden", zuletzt: null },
    { bereich: "Stil des Hauses", eintraege: z.stil.aktiv > 0 ? `${z.stil.aktiv} aktiv` : "keiner aktiv", wirkt: "Angebotsentwurf", zuletzt: z.stil.zuletzt },
    { bereich: "Wissen für den Website-Chat", eintraege: `${z.websiteChat.veroeffentlicht} veröffentlicht, ${z.websiteChat.intern} intern`, wirkt: "öffentlicher Chat (veröffentlichte), Angebotsentwurf (interne)", zuletzt: z.websiteChat.zuletzt },
  ];

  return (
    <div className="flex flex-col gap-6" data-testid="gedaechtnis-ueberblick">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
        <KpiCard label="Fakten über Ihren Betrieb" value={z.fakten.bestaetigt} icon="lightbulb" delta={neuFakten ? { text: `+${neuFakten} neu`, tone: "up" } : undefined} />
        <KpiCard label={`Muster beobachtet${z.einsichten.vorschlaege ? ` · ${z.einsichten.vorschlaege} Vorschlag` : ""}`} value={z.einsichten.aktiv} icon="insights" delta={neuMuster ? { text: `+${neuMuster} neu`, tone: "up" } : undefined} />
        <KpiCard label={`Regeln aktiv · ${z.regeln.entwurf} Entwurf`} value={z.regeln.aktiv} icon="rule" to="/gedaechtnis?tab=handelt" />
        <KpiCard label={`Positionen im Katalog, ${z.katalog.ausKiImport} aus KI-Import`} value={z.katalog.positionen} icon="menu_book" to="/katalog" />
        <KpiCard
          label="Stil des Hauses"
          value={z.stil.entwurf > 0 ? "Freigabe" : z.stil.aktiv > 0 ? "Aktiv" : "—"}
          icon="brush"
          iconTone={z.stil.entwurf > 0 ? "warn" : "primary"}
          delta={z.stil.entwurf > 0 ? { text: "wartet", tone: "warn" } : undefined}
          to="/gedaechtnis?tab=lernt"
        />
      </div>

      {d.lernen ? <LernKennzahlKarte k={d.lernen} /> : null}

      <Card className="space-y-3 p-5">
        <h2 className="flex items-center gap-2 font-body-lg text-body-lg font-semibold text-on-background dark:text-inverse-on-surface">
          <Icon name="new_releases" className="text-primary" />
          {d.zuletztBesucht && seit?.wert ? `Neu seit Ihrem letzten Besuch (${datum(seit.wert)})` : "Neu in den letzten 30 Tagen"}
        </h2>
        {d.neu.length === 0 ? (
          <p className="font-body-sm text-body-sm text-on-surface-variant">Seitdem hat MAESTRO nichts Neues gelernt.</p>
        ) : (
          <div className="flex flex-col gap-3">
            {d.neu.map((e) => (
              <Eintrag key={`${e.art}-${e.id}`} e={e} darfVerwalten={d.darfVerwalten} onFertig={q.reload} />
            ))}
          </div>
        )}
      </Card>

      <div className="grid gap-6 lg:grid-cols-2">
        <Betriebsprofil profil={d.betriebsprofil} darfVerwalten={d.darfVerwalten} onFertig={q.reload} />
        <Card className="space-y-3 p-5">
          <h2 className="flex items-center gap-2 font-body-lg text-body-lg font-semibold text-on-background dark:text-inverse-on-surface">
            <Icon name="bolt" className="text-primary" /> Gemessene Wirkung der Regeln
          </h2>
          {d.wirkung.length === 0 ? (
            <p className="font-body-sm text-body-sm text-on-surface-variant">Noch nichts gemessen. Sobald eine aktive Regel genug Vorgänge berührt hat, steht hier, wie oft Vorschläge vorher und nachher verworfen wurden.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left font-body-sm text-body-sm">
                <thead>
                  <tr className="text-[11.5px] uppercase tracking-wide text-on-surface-variant">
                    <th className="py-2 pr-3">Regel</th>
                    <th className="py-2 pr-3">verworfen vorher</th>
                    <th className="py-2">nachher</th>
                  </tr>
                </thead>
                <tbody>
                  {d.wirkung.map((w) => (
                    <tr key={w.id} className="border-t border-outline-variant/40 dark:border-white/10">
                      <td className="py-2 pr-3">{w.name}</td>
                      <td className="py-2 pr-3 tabular-nums">{quote(w.vorherQuote)}</td>
                      <td className="py-2 tabular-nums">{quote(w.nachherQuote)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <p className="text-[12px] text-on-surface-variant">Gezählt wird nur, was sich aus echten Vorgängen belegen lässt — keine Schätzung.</p>
        </Card>
      </div>

      <Card className="space-y-3 p-5">
        <h2 className="flex items-center gap-2 font-body-lg text-body-lg font-semibold text-on-background dark:text-inverse-on-surface">
          <Icon name="hub" className="text-primary" /> Was der Assistent beim Arbeiten nutzt
        </h2>
        <div className="overflow-x-auto">
          <table className="w-full text-left font-body-sm text-body-sm">
            <thead>
              <tr className="text-[11.5px] uppercase tracking-wide text-on-surface-variant">
                <th className="py-2 pr-3">Bereich</th>
                <th className="py-2 pr-3">Einträge</th>
                <th className="py-2 pr-3">wirkt in</th>
                <th className="py-2">zuletzt geändert</th>
              </tr>
            </thead>
            <tbody>
              {bereiche.map((b) => (
                <tr key={b.bereich} className="border-t border-outline-variant/40 dark:border-white/10">
                  <td className="py-2 pr-3">{b.bereich}</td>
                  <td className="py-2 pr-3 tabular-nums">{b.eintraege}</td>
                  <td className="py-2 pr-3">{b.wirkt}</td>
                  <td className="py-2 tabular-nums">{datum(b.zuletzt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Card className="space-y-3 p-5">
        <h2 className="flex items-center gap-2 font-body-lg text-body-lg font-semibold text-on-background dark:text-inverse-on-surface">
          <Icon name="report" className="text-primary" /> Braucht Ihren Blick
        </h2>
        {d.hinweise.length === 0 && z.einsichten.vorschlaege === 0 && z.stil.entwurf === 0 && z.regeln.entwurf === 0 ? (
          <p className="font-body-sm text-body-sm text-on-surface-variant">Gerade nichts.</p>
        ) : (
          <ul className="flex flex-col gap-2">
            {z.einsichten.vorschlaege > 0 && (
              <li className="rounded-xl bg-[#F0EAE0] px-3 py-2 text-[13px] text-[#6F675A] dark:bg-white/[0.08] dark:text-[#b8ae9e]">
                {z.einsichten.vorschlaege} Muster aus Absagegründen von Gästen warten auf Ihre Bestätigung (unter „Was MAESTRO weiß“).
              </li>
            )}
            {z.stil.entwurf > 0 && (
              <li className="rounded-xl bg-[#F0EAE0] px-3 py-2 text-[13px] text-[#6F675A] dark:bg-white/[0.08] dark:text-[#b8ae9e]">
                Ein neuer Stil des Hauses wartet auf Freigabe — bis dahin gilt der bisherige.
              </li>
            )}
            {z.regeln.entwurf > 0 && (
              <li className="rounded-xl bg-[#F0EAE0] px-3 py-2 text-[13px] text-[#6F675A] dark:bg-white/[0.08] dark:text-[#b8ae9e]">
                {z.regeln.entwurf} Regel-Entwurf{z.regeln.entwurf === 1 ? "" : "e"} noch nicht aktiv.
              </li>
            )}
            {d.hinweise.map((h) => (
              <li key={h.id} className="rounded-xl bg-[#F0EAE0] px-3 py-2 text-[13px] text-[#6F675A] dark:bg-white/[0.08] dark:text-[#b8ae9e]">
                Fakt „{h.text}“ wurde seit {datum(h.seit)} nicht bestätigt — vielleicht veraltet.
              </li>
            ))}
          </ul>
        )}
        <p className="text-[12px] text-on-surface-variant">Gelöschtes kommt nicht zurück: MAESTRO merkt sich gelöschte und bestrittene Einträge und lernt sie nicht erneut.</p>
      </Card>
    </div>
  );
}
